import type {
	GridDetailExpandChangeEvent,
	GridEditChangeEvent,
	GridItemChangeEvent,
} from "@tql/ui-components-react/kendo/grid";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useUpdateAddressBookItem } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { useNotifications } from "~/shared/hooks/use-notifications.ts";
import type {
	AddressBookItemSummary,
	AddressBookUpdateBodyV2,
} from "~/shared/schemas/address-book-schema.ts";

export type EditDescriptor = Record<number, boolean | string[]>;
export type DetailExpandDescriptor = Record<number, boolean> | undefined;

type UseAddressEditingProps = {
	data: AddressBookItemSummary[];
	setData: React.Dispatch<React.SetStateAction<AddressBookItemSummary[]>>;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
	showModal: boolean;
	stopType: 1 | 2;
};

export function useAddressEditing({
	setData,
	setIsLoading,
	showModal,
	stopType,
}: UseAddressEditingProps) {
	const updateAddressBookItemMutation = useUpdateAddressBookItem();
	const { showSuccess, showError } = useNotifications();

	const [edit, setEdit] = useState<EditDescriptor>({});
	const [detailExpand, setDetailExpand] =
		useState<DetailExpandDescriptor>(undefined);
	const [originalData, setOriginalData] = useState<
		Record<number, AddressBookItemSummary>
	>({});

	const isEditMode = useMemo(() => Object.values(edit).includes(true), [edit]);

	useEffect(() => {
		if (!showModal) {
			setEdit({});
			setOriginalData({});
		}
	}, [showModal]);

	const enterEdit = useCallback((dataItem: AddressBookItemSummary) => {
		setOriginalData((prev) => ({
			...prev,
			[dataItem.addressBookItemId]: { ...dataItem },
		}));
		setEdit({ [dataItem.addressBookItemId]: true });
	}, []);

	const handleSaveEdit = useCallback(
		(dataItem: AddressBookItemSummary) => {
			setDetailExpand(undefined);
			setIsLoading(true);
			setData((prev) =>
				prev.map((item) =>
					item.addressBookItemId === dataItem.addressBookItemId
						? dataItem
						: item,
				),
			);
			setEdit((prev) => ({
				...prev,
				[dataItem.addressBookItemId]: false,
			}));
			setOriginalData((prev) => {
				const newOriginal = { ...prev };
				delete newOriginal[dataItem.addressBookItemId];
				return newOriginal;
			});

			// Build V2 update payload with details array
			const updateData: AddressBookUpdateBodyV2 = {
				addressBookItemId: dataItem.addressBookItemId,
				customerId: dataItem.customerId,
				companyName: dataItem.companyName ?? undefined,
				address1: dataItem.address1,
				address2: dataItem.address2 ?? undefined,
				postalCode: dataItem.postalCode ?? undefined,
				city: dataItem.city ?? undefined,
				stateProvince: dataItem.stateProvince ?? undefined,
				country: dataItem.country,
				isValidated: dataItem.isValidated ?? undefined,
				legacyAddressId: dataItem.legacyAddressId ?? undefined,
				details: dataItem.details.map((detail) => {
					let accessorials: string | undefined;
					if (typeof detail.accessorials === "string") {
						accessorials = detail.accessorials;
					} else if (detail.accessorials) {
						accessorials = JSON.stringify(detail.accessorials);
					}

					return {
						contactDetailId: detail.contactDetailId,
						addressBookItemId: detail.addressBookItemId,
						contactName: detail.contactName ?? undefined,
						contactPhone: detail.contactPhone ?? undefined,
						contactExtension: detail.contactExtension,
						hoursOpen: detail.hoursOpen ?? undefined,
						hoursClose: detail.hoursClose ?? undefined,
						stopType: detail.stopType,
						mode: detail.mode,
						timeZone: detail.timeZone ?? undefined,
						specialInstructions: detail.specialInstructions ?? "",
						accessorials,
					};
				}),
			};

			const resolveDisplayName = () => {
				const trimmedName = dataItem.companyName?.trim();
				return trimmedName && trimmedName.length > 0 ? trimmedName : "Address";
			};

			updateAddressBookItemMutation.mutate(updateData, {
				onSuccess: () => {
					setIsLoading(false);
					showSuccess(`${resolveDisplayName()} updated in the address book.`);
				},
				onError: (error) => {
					setIsLoading(false);
					const originalItem = originalData[dataItem.addressBookItemId];
					if (originalItem) {
						setData((prevData) =>
							prevData.map((item) =>
								item.addressBookItemId === dataItem.addressBookItemId
									? originalItem
									: item,
							),
						);
					}
					showError(
						`Unable to update ${resolveDisplayName()}. Please try again shortly.`,
					);
					console.error("Failed to update address book item:", error);
				},
			});
		},
		[
			updateAddressBookItemMutation,
			originalData,
			setData,
			setIsLoading,
			showError,
			showSuccess,
		],
	);

	const handleCancelEdit = useCallback(
		(dataItem: AddressBookItemSummary) => {
			const originalItem = originalData[dataItem.addressBookItemId];
			if (originalItem) {
				setData((prev) =>
					prev.map((item) =>
						item.addressBookItemId === dataItem.addressBookItemId
							? originalItem
							: item,
					),
				);
			}
			setEdit((prev) => ({
				...prev,
				[dataItem.addressBookItemId]: false,
			}));
			setOriginalData((prev) => {
				const newOriginal = { ...prev };
				delete newOriginal[dataItem.addressBookItemId];
				return newOriginal;
			});
			// Collapse the row when canceling edit
			setDetailExpand((prev) => ({
				...prev,
				[dataItem.addressBookItemId]: false,
			}));
		},
		[originalData, setData],
	);

	const itemChange = useCallback(
		(event: GridItemChangeEvent) => {
			const { field } = event;
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Kendo Grid event value can be any type
			const value = event.value;
			const dataItem = event.dataItem as AddressBookItemSummary;

			if (!field) return;

			setData((prevData) =>
				prevData.map((item) => {
					if (item.addressBookItemId !== dataItem.addressBookItemId) {
						return item;
					}

					// Check if field exists in details array items
					const firstDetail = item.details[0];
					const isDetailField =
						firstDetail !== undefined && field in firstDetail;

					if (isDetailField) {
						// Only update the detail with the matching stopType (1 for pickup, 2 for delivery)
						const updatedDetails = item.details.map((detail) => {
							if (detail.stopType !== stopType) {
								return detail;
							}
							return {
								...detail,
								// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Dynamic field assignment from Kendo Grid
								[field]: value,
							};
						});
						return {
							...item,
							details: updatedDetails,
						};
					}

					// Update top-level field
					return {
						...item,
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Dynamic field assignment from Kendo Grid
						[field]: value,
					};
				}),
			);
		},
		[setData, stopType],
	);

	const handleExpandChange = useCallback(
		(event: GridDetailExpandChangeEvent) => {
			setDetailExpand(event.detailExpand);
		},
		[],
	);
	const handleEditChange = useCallback((event: GridEditChangeEvent) => {
		const editedRowId = Object.entries(event.edit).find(
			([, value]) =>
				value === true || (Array.isArray(value) && value.length > 0),
		)?.[0];

		if (editedRowId) {
			setDetailExpand((prev) => ({
				...prev,
				[editedRowId]: true,
			}));
		}
	}, []);

	// Applies a postal-code selection to a row, updating city/state/zip/country in
	// one pass. The grid's derived `cityStateZip` display recomputes from these.
	const applyAddressFields = useCallback(
		(
			dataItem: AddressBookItemSummary,
			fields: {
				city: string;
				stateProvince: string;
				postalCode: string;
				country: string;
			},
		) => {
			setData((prevData) =>
				prevData.map((item) =>
					item.addressBookItemId === dataItem.addressBookItemId
						? {
								...item,
								city: fields.city,
								stateProvince: fields.stateProvince,
								postalCode: fields.postalCode,
								country: fields.country,
							}
						: item,
				),
			);
		},
		[setData],
	);

	return {
		edit,
		isEditMode,
		detailExpand,
		handleExpandChange,
		enterEdit,
		handleSaveEdit,
		handleCancelEdit,
		itemChange,
		handleEditChange,
		applyAddressFields,
	};
}
